import type { Locale } from '@xdocs/i18n';

/**
 * STOP-GAP local catalog for `apps/web`-shell-only copy (app-shell nav,
 * connection banner §0.1 vocabulary, 404, a couple of form labels the
 * shared `auth`/`common`/`datatable` scopes don't cover yet).
 *
 * This is NOT a second i18n runtime (E18/§13.3 ban) — it reuses the exact
 * same ICU-via-`intl-messageformat` mechanism as `@xdocs/i18n` (see
 * `./useWebT.ts`), just physically scoped to `apps/web/**` because
 * `packages/i18n/**` (where these keys structurally belong, as a `web`
 * catalog scope) is outside this task's `apps/web/**` ownership boundary.
 * Flagged prominently as an open issue: the `@xdocs/i18n` (F0j) owner
 * should fold this file's keys into `packages/i18n/src/catalogs/{en,ro}/
 * web.json` and delete this file + `useWebT`, at which point every
 * `t('web:…')` call site here becomes a plain `useI18n().t('web:…')` call.
 */
/**
 * `en` defines the catalog SHAPE (mirrors `@xdocs/i18n`'s own `en/index.ts`
 * convention exactly, §20.1/§20.7) — `ro` is typed against its exact key
 * set below so a missing translation is a compile-time error, not a silent
 * runtime fallback.
 */
const en = {
  'shell.nav.documents': 'Documents',
  'shell.nav.explorer': 'Explorer',
  'shell.nav.templates': 'Templates',
  'shell.nav.discover': 'Discover',
  'shell.nav.settings': 'Settings',
  'shell.nav.personas': 'Personas',
  'shell.nav.activity': 'Activity',
  'shell.logout': 'Log out',
  'shell.logout.confirming': 'Logging out…',
  'shell.search.placeholder': 'Search documents, shares…',
  'shell.search.shortcutLabel': 'Open search (Command K)',
  'shell.menu.trigger': 'Account menu',
  'shell.menu.myShares': 'My shares',
  'shell.menu.styleguide': 'Style guide',
  'shell.menu.adminConsole': 'Admin console',
  'shell.menu.staffBadge': 'STAFF',
  'shell.menu.language': 'Language',
  'connection.connected': 'Connected',
  'connection.reconnecting': 'Reconnecting…',
  'connection.backOnline': 'Back online',
  'connection.offline': 'You’re offline',
  'action.toggleTheme': 'Toggle theme',
  'credits.label': 'Credits',
  'credits.count': '{count} cr',
  'notFound.eyebrow': '404 · Not found',
  'notFound.title': 'No such page.',
  // mockup-36: straight apostrophes verbatim (mock departs from the app's
  // usual curly-quote copy style for this string).
  'notFound.description': "That route doesn't exist — but you're never at a dead end.",
  'notFound.cta': 'Go to Documents',
  'notFound.ctaSecondary': 'Discover',
  'appError.eyebrow': 'Something went wrong',
  'appError.title': 'This view hit an error.',
  'appError.description': 'A part of the app crashed while rendering. Your work is safe and autosaved — reloading usually fixes it.',
  'appError.cta': 'Reload view',
  'appError.ctaSecondary': 'Report problem',
  'appError.reported': 'Technical details copied — thanks for the report.',
  'appError.details.label': 'Technical details',
  'appError.details.ref': 'ref: {ref} · build {build}',
  'documents.singular': 'document',
  'documents.emptyDescription':
    'Generate, expand, and share AI-enhanced documents — start with your first one.',
  'signup.displayName.label': 'Display name (optional)',
  'auth.banner.dismiss': 'Dismiss',
} as const;

const ro: Record<keyof typeof en, string> = {
  'shell.nav.documents': 'Documente',
  'shell.nav.explorer': 'Explorator',
  'shell.nav.templates': 'Șabloane',
  'shell.nav.discover': 'Descoperă',
  'shell.nav.settings': 'Setări',
  'shell.nav.personas': 'Persoane',
  'shell.nav.activity': 'Activitate',
  'shell.logout': 'Ieși din cont',
  'shell.logout.confirming': 'Se iese din cont…',
  'shell.search.placeholder': 'Caută documente, partajări…',
  'shell.search.shortcutLabel': 'Deschide căutarea (Command K)',
  'shell.menu.trigger': 'Meniu de cont',
  'shell.menu.myShares': 'Distribuirile mele',
  'shell.menu.styleguide': 'Ghid de stil',
  'shell.menu.adminConsole': 'Consolă admin',
  'shell.menu.staffBadge': 'STAFF',
  'shell.menu.language': 'Limbă',
  'connection.connected': 'Conectat',
  'connection.reconnecting': 'Se reconectează…',
  'connection.backOnline': 'Din nou online',
  'connection.offline': 'Ești offline',
  'action.toggleTheme': 'Comută tema',
  'credits.label': 'Credite',
  'credits.count': '{count} cr',
  'notFound.eyebrow': '404 · Pagină negăsită',
  'notFound.title': 'Nicio astfel de pagină.',
  'notFound.description': 'Ruta aceasta nu există — dar nu ești niciodată într-o fundătură.',
  'notFound.cta': 'Mergi la Documente',
  'notFound.ctaSecondary': 'Descoperă',
  'appError.eyebrow': 'Ceva n-a mers bine',
  'appError.title': 'Această vizualizare a întâmpinat o eroare.',
  'appError.description': 'O parte a aplicației s-a blocat la randare. Munca ta este în siguranță și salvată automat — o reîncărcare rezolvă de obicei problema.',
  'appError.cta': 'Reîncarcă vizualizarea',
  'appError.ctaSecondary': 'Raportează problema',
  'appError.reported': 'Detaliile tehnice au fost copiate — mulțumim pentru raportare.',
  'appError.details.label': 'Detalii tehnice',
  'appError.details.ref': 'ref: {ref} · build {build}',
  'documents.singular': 'document',
  'documents.emptyDescription':
    'Generează, extinde și distribuie documente îmbunătățite cu AI — începe cu primul tău document.',
  'signup.displayName.label': 'Nume afișat (opțional)',
  'auth.banner.dismiss': 'Închide',
};

export const WEB_CATALOG: Record<Locale, Record<keyof typeof en, string>> = { en, ro };

/** The literal key union `en` defines — the type-safe half of `WebMessageId` (see `useWebT.ts`). */
export type WebCatalogKey = keyof typeof en;
